import {
  Box,
  Container,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import Card from "../components/privacy/Card";
import Nav from "../components/navComponents/Nav";
import Section from "../components/privacy/Section";

import { theme } from "../utils/theme";

const COMPANY_NAME = "MediLOT Technologies Pte Ltd";
const EFFECTIVE_DATE = "January 1, 2026";
const LAST_UPDATED = "January 12, 2026";
const DPO_EMAIL = "dpo@medilot.ai";
const COMPANY_ADDRESS =
  "3791 Jalan Bukit Merah, #08-12, E-Centre @ Redhill, Singapore (159471)";

const PrivacyPage = () => {
  return (
    <Box>
      <Nav contained={true} />

      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          py: { xs: 24, md: 32 },
        }}
      >
        {/* Header */}
        <Box component="header">
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontSize: {
                xs: theme.typography.h2,
                md: theme.typography.h1,
              },
              fontWeight: 600,
              letterSpacing: 0.9,
              lineHeight: 1.1,
              textTransform: "capitalize",
            }}
          >
            Privacy Policy
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            color: theme.palette.grays[900],
            flexDirection: "column",
            fontSize: theme.typography.h5,
            fontWeight: 300,
            gap: 2,
          }}
        >
          <Typography variant="body2">
            Effective Date: <strong>{EFFECTIVE_DATE}</strong> &nbsp;|&nbsp; Last
            Updated: <strong>{LAST_UPDATED}</strong>
          </Typography>

          <Typography variant="body2">
            This Privacy Policy applies to Medilot services provided by{" "}
            <strong>{COMPANY_NAME}</strong> (“Medilot”, “we”, “our”, or “us”).
          </Typography>
        </Box>

        {/* Intro Card */}
        <Card>
          <Typography
            sx={{
              color: theme.palette.grays[900],
              fontSize: theme.typography.h5,
              mb: 3,
            }}
          >
            This Privacy Policy sets out the manner in which{" "}
            <strong>{COMPANY_NAME}</strong> (“we”, “our”, or “us”) collects,
            uses, discloses, stores, transfers, and protects personal data in
            connection with our platforms, applications, services, application
            programming interfaces (APIs), software development kits (SDKs), and
            related offerings (collectively, the “Services”).
          </Typography>

          <Typography
            sx={{
              color: theme.palette.grays[900],
              fontSize: theme.typography.h5,
            }}
          >
            We are committed to complying with the{" "}
            <strong>
              Personal Data Protection Act 2012 of Singapore (PDPA)
            </strong>{" "}
            and aligning our practices with internationally recognised personal
            data protection frameworks, including principles reflected in the{" "}
            <strong>EU GDPR</strong> and other applicable international privacy
            laws.
          </Typography>
        </Card>

        {/* Sections */}
        <Section title="1. Definitions">
          “<strong>Personal Data</strong>” has the meaning given under the PDPA
          and refers to data, whether true or not, about an individual who can
          be identified from that data or from that data and other information
          to which we have or are likely to have access. “
          <strong>Health and Biometric Data</strong>” refers to personal data
          relating to an individual’s physical, physiological, or wellness
          characteristics, including activity, sleep, and physiological
          measurements. “<strong>Processing</strong>” refers to the collection,
          use, disclosure, storage, transfer, or other handling of personal
          data.
        </Section>

        <Section title="2. Scope of Application">
          This Privacy Policy applies to individuals who use the Services,
          individuals whose personal data is processed through connected
          devices, sensors, or integrated third-party platforms, and enterprise,
          institutional, or research users of the Services. This Privacy Policy
          does not apply to third-party platforms, devices, or services that are
          governed by their own privacy policies.
        </Section>

        <Section title="3. Categories of Personal Data Collected">
          Depending on the nature of the Services used and the permissions
          granted, we may collect and process personal data including:
          <List
            sx={{
              listStyleType: "disc",
              pl: 6,
            }}
          >
            <ListItem sx={{ display: "list-item" }}>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: theme.palette.grays[900],
                      fontSize: `${theme.typography.h5} !important`,
                    }}
                  >
                    <strong>Identification and contact information</strong> such
                    as name, email address, account identifiers, and contact
                    details;
                  </Typography>
                }
              />
            </ListItem>

            <ListItem sx={{ display: "list-item" }}>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: theme.palette.grays[900],
                      fontSize: `${theme.typography.h5} !important`,
                    }}
                  >
                    <strong>Activity, usage, and technical data</strong> such as
                    application usage logs, interaction records, device
                    identifiers, IP addresses, and system diagnostics; and
                  </Typography>
                }
              />
            </ListItem>

            <ListItem sx={{ display: "list-item" }}>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: theme.palette.grays[900],
                      fontSize: `${theme.typography.h5} !important`,
                    }}
                  >
                    <strong>Health and biometric data</strong> (where explicitly
                    authorised and permitted by applicable law) such as physical
                    activity and movement data, sleep-related metrics,
                    physiological measurements (including heart rate), and
                    user-reported lifestyle or wellness information.
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Section>

        <Section title="4. Sources of Personal Data">
          Personal data may be collected directly from individuals, from
          connected devices and sensors, from integrated third-party platforms
          or partners subject to user consent and contractual controls, and from
          system-generated records arising from the use of the Services.
        </Section>

        <Section title="5. Purpose Limitation and Use of Personal Data">
          In accordance with the PDPA purpose limitation obligation, personal
          data is collected, used, and disclosed only for purposes that a
          reasonable person would consider appropriate in the circumstances and
          that have been communicated to the individual. These purposes include
          providing, operating, and maintaining the Services; delivering
          authorised insights, summaries, and analytics; supporting caregiving,
          wellness, or clinical workflows where applicable; ensuring system
          security, performance monitoring, and service improvement; and
          complying with applicable legal, regulatory, and contractual
          obligations. Personal data is not sold, and health or biometric data
          is not used for advertising purposes.
        </Section>

        <Section title="6. Legal Basis for Processing and Consent Management">
          Personal data is processed based on one or more lawful grounds,
          including consent provided by the individual, contractual necessity
          for service delivery, compliance with legal obligations, or legitimate
          interests where permitted under applicable law. Explicit consent is
          obtained prior to the processing of health or biometric data.
          Individuals may withdraw consent at any time, subject to legal or
          contractual restrictions, and acknowledge that such withdrawal may
          affect the availability or functionality of certain Services.
        </Section>

        <Section title="7. Notification, Transparency, and Accountability">
          Individuals are informed, prior to or at the time of collection, of
          the categories of personal data collected, the purposes for which the
          data is processed, and the manner in which it may be used or
          disclosed. We maintain internal data protection policies and
          procedures, designate a<strong>Data Protection Officer (DPO)</strong>,
          and periodically review our data protection practices in accordance
          with the accountability obligations under the PDPA.
        </Section>

        <Section title="8. Disclosure of Personal Data">
          Personal data is disclosed only where necessary and subject to
          appropriate safeguards. Disclosure may be made to third-party service
          providers who process data on our behalf under binding confidentiality
          and data protection obligations; to enterprise or institutional
          partners where Services are delivered through such organisations, in
          accordance with user consent, contractual arrangements, and role-based
          access controls; or where disclosure is required by applicable law,
          regulation, court order, or lawful governmental request.
        </Section>

        <Section title="9. Access, Correction, and Accuracy">
          Individuals may request access to their personal data or request
          correction of inaccurate or incomplete data in accordance with the
          PDPA. We take reasonable steps to ensure that personal data used or
          disclosed is accurate, complete, and up to date.
        </Section>

        <Section title="10. Access Controls and Health Data Safeguards">
          Access to personal data, including health and biometric data, is
          restricted through role-based access controls, least-privilege
          principles, and appropriate authentication and audit mechanisms.
          Different categories of users, such as end users, caregivers,
          clinicians, or administrators, may be granted access to different
          levels of data in accordance with applicable consent, authorisation,
          and contractual arrangements.
        </Section>

        <Section title="11. Protection and Security Measures">
          We implement reasonable administrative, technical, and organisational
          measures to protect personal data against unauthorised access,
          collection, use, disclosure, copying, modification, or disposal. These
          measures include encryption of data in transit and at rest, secure
          authentication mechanisms, and monitoring and incident response
          procedures. Enhanced safeguards may be applied to health and biometric
          data where appropriate.
        </Section>

        <Section title="12. Retention Limitation">
          Personal data is retained only for as long as it is necessary to
          fulfil the purposes for which it was collected or to comply with
          applicable legal or contractual obligations. Where appropriate,
          personal data may be anonymised or aggregated for permitted long-term
          analytical, reporting, or research purposes.
        </Section>

        <Section title="13. Cross-Border Transfers">
          Where personal data is transferred outside Singapore, we take
          reasonable steps to ensure that the recipient provides a standard of
          protection that is comparable to the protection required under the
          PDPA, including through contractual, organisational, and technical
          safeguards.
        </Section>

        <Section title="14. Children and Minors">
          The Services are not intended for use by minors unless such use is
          explicitly authorised and governed by applicable laws, institutional
          policies, or parental or guardian consent requirements.
        </Section>

        <Section title="15. Updates to This Privacy Policy">
          We may update this Privacy Policy from time to time to reflect changes
          in legal requirements, regulatory guidance, or our data processing
          practices. Any updated version will be made available with a revised
          effective date and, where appropriate, communicated to individuals.
        </Section>

        <Section title="16. Contact Information">
          For questions, requests, or concerns relating to personal data
          protection, including requests for access, correction, or withdrawal
          of consent, please contact:
          <Card>
            <Typography
              sx={{
                color: theme.palette.grays[900],
                fontSize: theme.typography.h5,
              }}
            >
              <strong>Data Protection Officer (DPO)</strong>
              <br />
              <strong>{COMPANY_NAME}</strong>
              <br />
              Email: <Link to={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</Link>
              <br />
              Address: <strong>{COMPANY_ADDRESS}</strong>
            </Typography>
          </Card>
        </Section>
      </Container>
    </Box>
  );
};

export default PrivacyPage;
